import React from "react";
import { useRenderTemplateWithCollections } from "../MacroBindingContext";

export interface MacroButtonLinkBlockProps {
  text: string;
  href: string;
  backgroundColor: string;
  textColor: string;
  title?: string;
}

const MacroButtonLinkBlock = {
  label: "Button Link",
  llmDescription: `A call-to-action button link for emails.
- 'text' (string): The button text
- 'href' (string): The URL to link to
- 'backgroundColor' (string): Background color (hex code like #0d6efd)
- 'textColor' (string): Text color (hex code like #ffffff)
- 'title' (string, optional): Tooltip text
- Supports {{variable}} placeholders in text and href
- Inside repeaters, also supports collection bindings like {{program.Name}}, {{room.RoomType}}
- Use {{QuoteLink}} for the reservation quote URL
- Styled with inline styles for email client compatibility
- Perfect for "View Your Quote Now" or "Book Now" buttons`,
  fields: {
    text: {
      type: "text" as const,
      label: "Button Text",
    },
    href: {
      type: "text" as const,
      label: "URL",
    },
    backgroundColor: {
      type: "text" as const,
      label: "Background Color",
    },
    textColor: {
      type: "text" as const,
      label: "Text Color",
    },
    title: {
      type: "text" as const,
      label: "Title (tooltip)",
    },
  },
  defaultProps: {
    text: "View Your Quote Now",
    href: "{{QuoteLink}}",
    backgroundColor: "#0d6efd",
    textColor: "#ffffff",
    title: "View Your Quote",
  },
  render: (props: MacroButtonLinkBlockProps) => {
    const render = useRenderTemplateWithCollections();
    const renderedText = render(props.text);
    const renderedHref = render(props.href);
    const renderedTitle = props.title ? render(props.title) : undefined;

    // Inline styles for email client compatibility
    const buttonStyle: React.CSSProperties = {
      backgroundColor: props.backgroundColor,
      borderRadius: "6px",
      color: props.textColor,
      display: "inline-block",
      textDecoration: "none",
      padding: "10px 16px",
      textAlign: "center",
      fontWeight: "bold",
    };

    return (
      <a
        href={renderedHref}
        title={renderedTitle}
        target="_blank"
        rel="noopener noreferrer"
        style={buttonStyle}
      >
        {renderedText}
      </a>
    );
  },
};

export default MacroButtonLinkBlock;
